import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGeoRatings, setGeoRatingCountries, refreshGeoRatings,
  type GeoRating, type PlayApp,
} from '@/api/play';
import { scoreTone, formatAgo } from '@/lib/geoRating';
import { COUNTRY_MAP } from '@/lib/countries';
import { CountryMultiSelect } from '../CountryMultiSelect';
import { Modal } from '../Modal';
import { Button } from '../Button';

const TONE_CLASS = {
  good: 'text-green-400',
  ok: 'text-amber-400',
  bad: 'text-red-400',
} as const;

// Play's rating is per country: the same package can sit at 4.7 in BR and 4.2
// in RU. Rows arrive worst-first from the API, so the geos that need attention
// are already at the top.
export function GeoRatingsPanel({ app }: { app: PlayApp }) {
  const qc = useQueryClient();
  const key = ['play-geo-ratings', app.id];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);

  const q = useQuery({ queryKey: key, queryFn: () => getGeoRatings(app.id) });
  const rows: GeoRating[] = q.data ?? [];

  const refresh = useMutation({
    mutationFn: () => refreshGeoRatings(app.id),
    onSuccess: (data) => qc.setQueryData(key, data),
  });
  const save = useMutation({
    mutationFn: (codes: string[]) => setGeoRatingCountries(app.id, codes),
    onSuccess: (data) => { qc.setQueryData(key, data); setEditing(false); },
  });

  const lastFetched = rows.reduce((max, r) => (r.fetched_at && r.fetched_at > max ? r.fetched_at : max), 0);

  function openEditor() {
    setDraft(rows.map((r) => r.country.toUpperCase()));
    setEditing(true);
  }

  return (
    <div className="w-full xl:w-80 shrink-0 bg-surface border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-medium text-content">Rating by country</h2>
        <Button
          size="sm"
          variant="secondary"
          loading={refresh.isPending}
          disabled={rows.length === 0}
          onClick={() => refresh.mutate()}
        >
          Refresh
        </Button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-content-subtle">
          {lastFetched ? `updated ${formatAgo(lastFetched)}` : 'never fetched'}
        </span>
        <button onClick={openEditor} className="text-xs text-accent hover:underline">
          Edit list
        </button>
      </div>

      {refresh.isError && (
        <p className="text-xs text-red-400 mb-2">
          {(refresh.error as Error)?.message || 'refresh failed'}
        </p>
      )}

      {q.isLoading ? (
        <p className="text-xs text-content-muted">Loading…</p>
      ) : q.isError ? (
        <p className="text-xs text-red-400">{(q.error as Error)?.message || 'failed to load'}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-content-muted">
          No countries selected. Use “Edit list” to pick the markets you care about.
        </p>
      ) : (
        <div>
          {rows.map((r) => {
            const meta = COUNTRY_MAP[r.country.toUpperCase()];
            return (
              <div
                key={r.country}
                title={r.error ?? meta?.name ?? r.country}
                className="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-0"
              >
                <span className="text-base leading-none">{meta?.flag ?? '🏳'}</span>
                <span className="text-xs text-content-muted w-7">{r.country.toUpperCase()}</span>
                <span className="text-xs text-content-subtle truncate flex-1">{meta?.name ?? ''}</span>
                {r.error && <span className="text-xs text-red-400" aria-hidden>!</span>}
                {r.available === 0 ? (
                  <span className="text-xs text-content-subtle" title="not listed in this country">n/a</span>
                ) : r.score === null ? (
                  <span className="text-xs text-content-subtle">—</span>
                ) : (
                  <span className={`text-sm font-medium tabular-nums ${TONE_CLASS[scoreTone(r.score)]}`}>
                    {r.score.toFixed(2)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={editing} onClose={() => setEditing(false)} title="Countries to track">
        <p className="text-sm text-content-muted mb-3">
          One scraper request per country on every refresh — keep the list to the markets
          that matter. Clearing the list restores the default countries the next time
          this page loads.
        </p>
        <CountryMultiSelect value={draft} onChange={setDraft} />
        {save.isError && (
          <p className="text-red-400 text-sm mt-2">{(save.error as Error)?.message || 'save failed'}</p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() => save.mutate(draft.map((c) => c.toLowerCase()))}
          >
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
}
